import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import AddLinkOutlinedIcon from "@mui/icons-material/AddLinkOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MaterialDialog from "./MaterialDialog";
import { isMixedReviewEnabled, revisionV2Api } from "./revisionV2Api";
import { composeReviewSummary } from "./reviewSummaryProjection";
import MetaDialog from "./MetaDialog";
import RevisionKpis from "./RevisionKpis";
import RevisionLinksDialog from "./RevisionLinksDialog";
import RevisionExcelCellEditor from "./RevisionExcelCellEditor";
import useRevisionRegistroDraft from "./useRevisionRegistroDraft";
import useJornadaLocationContext from "./useJornadaLocationContext";
import {
  createRevisionRapidaSaludMaterial,
  getRevisionRapidaSaludMaterials,
  getRevisionRapidaSaludMeta,
  getRevisionRapidaSaludRecords,
  getRevisionRapidaSaludSummary,
  updateRevisionRapidaSaludMaterial,
  updateRevisionRapidaSaludMeta,
} from "./revisionRapidaSaludApi";
import { validateMarkErrorReason } from "./revisionRapidaSaludDetailUtils";
import {
  REVISION_EXCEL_COLUMN_BY_ID,
  REVISION_EXCEL_EDITABLE_COLUMNS,
  buildRevisionExcelRow,
  createRevisionExcelColumnVisibility,
  getRevisionExcelChangedColumns,
  getRevisionExcelFieldDefinition,
  getRevisionExcelFieldErrorKey,
  isRevisionExcelColumnEditable,
} from "./revisionRapidaSaludExcel";
import {
  buildRevisionRapidaSaludPath,
  buildRevisionRapidaSaludRecordPath,
  extractRevisionApiError,
  extractRevisionRecordsError,
  getStoredRevisionUser,
  getRevisionRecordActionLabel,
  getRevisionRecordStatus,
  normalizeRevisionRecordSearch,
  resolveUxCapability,
} from "./revisionRapidaSaludUtils";

const SEARCH_DEBOUNCE_MS = 400;
const V2CaptureList = React.lazy(() => import("./V2CaptureList"));
const SORT_FIELD_MAP = Object.freeze({ id: "id" });
const INITIAL_PAGINATION = { pageIndex: 0, pageSize: 25 };
const INITIAL_SORTING = [{ id: "id", desc: true }];

const formatDate = (value) => {
  if (!value) return "Sin fecha";
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-VE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const formatActivity = (activity) => {
  if (Array.isArray(activity)) return activity.join(" · ");
  return activity || "Convocatoria sin descripción";
};

const resolveUpdater = (updater, current) =>
  typeof updater === "function" ? updater(current) : updater;

export const getRevisionListLoadingState = ({
  listFetching,
  hasCompletedInitialFetch,
  recordCount,
}) => {
  const initialListLoading = Boolean(
    listFetching && !hasCompletedInitialFetch && recordCount === 0
  );

  return {
    initialListLoading,
    showSkeletons: initialListLoading,
    showProgressBars: Boolean(listFetching && recordCount > 0),
  };
};

export const getRevisionTableRowId = (row, index) =>
  row?.id !== null && row?.id !== undefined && row?.id !== ""
    ? String(row.id)
    : `loading-row-${index}`;

export const compactAgeLabel = (value) => {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  const match = text.match(/^(\d+)\s*año\(s\)\s*y?\s*(\d+)\s*mes\(es\)$/i);
  if (!match) return value || "Sin dato";
  const years = Number(match[1]);
  const months = Number(match[2]);
  return `${years} ${years === 1 ? "año" : "años"} ${months} ${months === 1 ? "mes" : "meses"}`;
};

export const getNextRevisionExcelCell = (
  row,
  currentColumnId,
  direction,
  { diagnosesEditable = false } = {}
) => {
  const currentIndex = REVISION_EXCEL_EDITABLE_COLUMNS
    .findIndex((config) => config.id === currentColumnId);
  if (currentIndex < 0) return null;

  for (let step = 1; step <= REVISION_EXCEL_EDITABLE_COLUMNS.length; step += 1) {
    const nextIndex = (
      currentIndex + direction * step + REVISION_EXCEL_EDITABLE_COLUMNS.length
    ) % REVISION_EXCEL_EDITABLE_COLUMNS.length;
    const config = REVISION_EXCEL_EDITABLE_COLUMNS[nextIndex];
    const target = row.getAllCells().find((item) => item.column.id === config.id);
    const diagnosisBlocked = config.kind === "slot" && config.key === "diagnoses" && !diagnosesEditable;
    if (target && !diagnosisBlocked) return target;
  }
  return null;
};

const PARTICIPANT_ALLOWED_ROLES = ["1", "2", "6", "8"];
const AgregarParticipantesConvocatoriaModal = React.lazy(() =>
  import("components/AgregarParticipantesConvocatoriaModal")
);
const VoluntariosConvocatoriaOperativaDialog = React.lazy(() =>
  import("components/VoluntariosConvocatoriaOperativaDialog")
);

const RegistrosRevisionRapidaSalud = ({ unified = false }) => {
  const { convocatoriaId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const recordsRequestRef = useRef(0);
  const summaryRequestRef = useRef(0);
  const auxiliaryRequestRef = useRef(0);
  const tableRef = useRef(null);
  const cellRefs = useRef({});
  const restoredListState = location.state?.listState || {};
  const stateContext = useMemo(() => location.state?.context || {}, [location.state]);
  const navigationConvocatoriaContext = location.state?.convocatoriaContext;
  const user = useMemo(() => getStoredRevisionUser(), []);

  const mixed = isMixedReviewEnabled();
  const [legacySummary, setSummary] = useState(null);
  const [mixedSummary, setMixedSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");
  const [records, setRecords] = useState([]);
  const [listFetching, setListFetching] = useState(true);
  const [recordsError, setRecordsError] = useState("");
  const [hasLoadedRecords, setHasLoadedRecords] = useState(false);
  const [serverPagination, setServerPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const [pagination, setPagination] = useState(() => restoredListState.pagination || INITIAL_PAGINATION);
  const [sorting, setSorting] = useState(() => restoredListState.sorting || INITIAL_SORTING);
  const [stateFilter, setStateFilter] = useState(() => restoredListState.stateFilter || "");
  const [sourceFilter, setSourceFilter] = useState(() => restoredListState.sourceFilter || "");
  const [searchInput, setSearchInput] = useState(() => restoredListState.searchInput || "");
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    normalizeRevisionRecordSearch(restoredListState.searchInput || "")
  );
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [activeRecordId, setActiveRecordId] = useState("");
  const [editingCell, setEditingCell] = useState(null);
  const [pendingCell, setPendingCell] = useState(null);
  const [pendingFocus, setPendingFocus] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState(createRevisionExcelColumnVisibility);
  const [listSnackbar, setListSnackbar] = useState("");
  const [markErrorOpen, setMarkErrorOpen] = useState(false);
  const [markErrorReason, setMarkErrorReason] = useState("");
  const [markErrorValidation, setMarkErrorValidation] = useState("");
  const [jornadaDetailContext, setJornadaDetailContext] = useState(null);
  const [meta, setMeta] = useState(null);
  const [metaContext, setMetaContext] = useState("");
  const [materials, setMaterials] = useState([]);
  const [auxiliaryLoading, setAuxiliaryLoading] = useState(false);
  const [metaError, setMetaError] = useState("");
  const [materialsError, setMaterialsError] = useState("");
  const [metaDialogOpen, setMetaDialogOpen] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaActionError, setMetaActionError] = useState("");
  const [linksDialogOpen, setLinksDialogOpen] = useState(false);
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialSaving, setMaterialSaving] = useState(false);
  const [materialActionError, setMaterialActionError] = useState("");
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [addParticipantsOpen, setAddParticipantsOpen] = useState(false);

  const summary = useMemo(() => mixed ? composeReviewSummary({
    convocatoriaId, legacy: legacySummary, mixed: mixedSummary,
    meta: String(metaContext) === String(convocatoriaId) ? meta : null,
  }) : legacySummary, [convocatoriaId, legacySummary, mixedSummary, meta, metaContext, mixed]);

  useEffect(() => {
    if (!mixed) return;
    setSummary(null); setMixedSummary(null); setMeta(null); setMetaContext(""); setMaterials([]);
  }, [convocatoriaId, mixed]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearch(normalizeRevisionRecordSearch(searchInput));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timerId);
  }, [searchInput]);

  const loadSummary = useCallback(async () => {
    const requestId = ++summaryRequestRef.current;
    setSummaryLoading(true);
    setSummaryError("");

    try {
      if (!mixed) {
        const response = await getRevisionRapidaSaludSummary(convocatoriaId);
        if (requestId === summaryRequestRef.current) setSummary(response);
      } else {
        const [legacy, combined] = await Promise.allSettled([
          getRevisionRapidaSaludSummary(convocatoriaId), revisionV2Api.summary(convocatoriaId),
        ]);
        if (requestId !== summaryRequestRef.current) return;
        if (legacy.status === "fulfilled") setSummary(legacy.value);
        if (combined.status === "fulfilled") setMixedSummary(combined.value);
        const errors = [];
        if (legacy.status === "rejected") errors.push(`Contexto: ${extractRevisionApiError(legacy.reason).message}`);
        if (combined.status === "rejected") errors.push(`Resumen mixto no disponible. Se conservan los \u00faltimos conteos disponibles: ${extractRevisionApiError(combined.reason).message}`);
        setSummaryError(errors.join(" "));
      }
    } catch (error) {
      if (requestId === summaryRequestRef.current) {
        if (!mixed) setSummary(null);
        setSummaryError(extractRevisionApiError(error).message);
      }
    } finally {
      if (requestId === summaryRequestRef.current) setSummaryLoading(false);
    }
  }, [convocatoriaId, mixed]);

  useEffect(() => {
    loadSummary();
    return () => {
      summaryRequestRef.current += 1;
    };
  }, [loadSummary, refreshVersion]);

  const loadAuxiliaryData = useCallback(async () => {
    if (!unified) return;
    const requestId = ++auxiliaryRequestRef.current;
    setAuxiliaryLoading(true);
    setMetaError("");
    setMaterialsError("");

    const [metaResult, materialsResult] = await Promise.allSettled([
      getRevisionRapidaSaludMeta(convocatoriaId),
      getRevisionRapidaSaludMaterials(convocatoriaId),
    ]);

    if (requestId !== auxiliaryRequestRef.current) return;

    if (metaResult.status === "fulfilled") {
      setMeta(metaResult.value);
      setMetaContext(convocatoriaId);
    } else {
      if (!mixed) setMeta(null);
      setMetaError(extractRevisionApiError(metaResult.reason).message);
    }

    if (materialsResult.status === "fulfilled") {
      setMaterials(Array.isArray(materialsResult.value) ? materialsResult.value : []);
    } else {
      if (!mixed) setMaterials([]);
      setMaterialsError(extractRevisionApiError(materialsResult.reason).message);
    }

    setAuxiliaryLoading(false);
  }, [convocatoriaId, unified, mixed]);

  useEffect(() => {
    if (!unified) return undefined;
    loadAuxiliaryData();
    return () => {
      auxiliaryRequestRef.current += 1;
    };
  }, [loadAuxiliaryData, refreshVersion, unified]);

  const backendSorting = sorting[0] || INITIAL_SORTING[0];

  useEffect(() => {
    const requestId = ++recordsRequestRef.current;
    setListFetching(true);
    setRecordsError("");
    if (mixed && sourceFilter === "V2_CAPTURE") {
      setRecords([]); setListFetching(false); setHasLoadedRecords(true);
      return undefined;
    }
    const params = {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      state: stateFilter === "" ? undefined : Number(stateFilter),
      source: sourceFilter || undefined,
      search: debouncedSearch || undefined,
      sort: SORT_FIELD_MAP[backendSorting.id] || "id",
      order: backendSorting.desc ? "DESC" : "ASC",
    };

    Promise.resolve(getRevisionRapidaSaludRecords(convocatoriaId, params))
      .then((response) => {
        if (requestId !== recordsRequestRef.current) return;
        setRecords(Array.isArray(response?.data) ? response.data : []);
        setServerPagination(response?.pagination || {
          page: 1,
          pageSize: pagination.pageSize,
          total: 0,
          totalPages: 0,
        });
        setHasLoadedRecords(true);
        setListFetching(false);
      })
      .catch((error) => {
        if (requestId !== recordsRequestRef.current) return;
        setRecordsError(extractRevisionRecordsError(error).message);
        setHasLoadedRecords(true);
        setListFetching(false);
      });

    return () => {
      if (requestId === recordsRequestRef.current) recordsRequestRef.current += 1;
    };
  }, [
    backendSorting.desc,
    backendSorting.id,
    convocatoriaId,
    debouncedSearch,
    pagination.pageIndex,
    pagination.pageSize,
    refreshVersion,
    sourceFilter,
    stateFilter,
    mixed,
  ]);

  const listState = useMemo(
    () => ({ pagination, sorting, stateFilter, sourceFilter, searchInput }),
    [pagination, searchInput, sorting, sourceFilter, stateFilter]
  );

  const handleRowMutationSuccess = useCallback(({ action, detail, message }) => {
    setListSnackbar(message);
    setRefreshVersion((current) => current + 1);
    const nextStatus = Number(detail?.status?.code ?? detail?.status);
    const remainsInFilter = stateFilter === "" || Number(stateFilter) === nextStatus;

    if (action === "save" && remainsInFilter) return;

    if (action === "save_and_verify") {
      const currentIndex = records.findIndex((record) => String(record.id) === String(activeRecordId));
      const nextRecord = records[currentIndex + 1] || records[currentIndex - 1];
      if (nextRecord) setPendingFocus({ rowId: String(nextRecord.id), columnId: "nombre" });
    }
    setEditingCell(null);
    setPendingCell(null);
    setActiveRecordId("");
  }, [activeRecordId, records, stateFilter]);

  const draftController = useRevisionRegistroDraft({
    convocatoriaId,
    registroId: activeRecordId,
    enabled: Boolean(activeRecordId),
    onMutationSuccess: handleRowMutationSuccess,
  });
  const draftControllerRef = useRef(draftController);
  draftControllerRef.current = draftController;

  useEffect(() => {
    setJornadaDetailContext(null);
  }, [convocatoriaId]);

  useEffect(() => {
    const detail = draftController.serverSnapshot;
    if (
      detail?.fields &&
      String(detail.convocatoriaId) === String(convocatoriaId)
    ) {
      setJornadaDetailContext(detail);
    }
  }, [convocatoriaId, draftController.serverSnapshot]);

  const validJornadaDetail = String(jornadaDetailContext?.convocatoriaId) === String(convocatoriaId)
    ? jornadaDetailContext
    : null;
  const {
    jornadaContext,
    convocatoriaContext: resolvedConvocatoriaContext,
  } = useJornadaLocationContext({
    convocatoriaId,
    navigationContext: navigationConvocatoriaContext,
    summary,
    summaryResolved: !summaryLoading,
    detail: validJornadaDetail,
  });
  const sharedNavigationState = useMemo(() => ({
    context: stateContext,
    convocatoriaContext: resolvedConvocatoriaContext,
  }), [resolvedConvocatoriaContext, stateContext]);

  const changedColumnIds = useMemo(
    () => getRevisionExcelChangedColumns(draftController.serverSnapshot, draftController.draft),
    [draftController.draft, draftController.serverSnapshot]
  );
  const changedColumnSet = useMemo(() => new Set(changedColumnIds), [changedColumnIds]);
  const hasFrontendErrors = Object.keys(draftController.fieldErrors).length > 0;

  const closeActiveEditor = useCallback(() => {
    setEditingCell(null);
    setPendingCell(null);
    setActiveRecordId("");
  }, []);

  const runGuarded = useCallback((execute, { closeEditor = true } = {}) => {
    if (draftController.dirty) {
      setPendingAction({ execute, closeEditor });
      return false;
    }
    if (closeEditor) closeActiveEditor();
    execute();
    return true;
  }, [closeActiveEditor, draftController.dirty]);

  const confirmPendingAction = () => {
    const action = pendingAction;
    setPendingAction(null);
    if (!action) return;
    if (action.closeEditor) closeActiveEditor();
    action.execute();
  };

  const findTableCell = useCallback((rowId, columnId, table = tableRef.current) => {
    const row = table?.getRowModel?.().rows.find((item) => String(item.id) === String(rowId));
    return row?.getAllCells?.().find((cell) => cell.column.id === columnId) || null;
  }, []);

  const focusStaticCell = useCallback((rowId, columnId) => {
    setTimeout(() => cellRefs.current[`${rowId}:${columnId}`]?.focus?.(), 0);
  }, []);

  const enterCellEditing = useCallback((rowId, columnId) => {
    const config = REVISION_EXCEL_COLUMN_BY_ID[columnId];
    if (!config || !isRevisionExcelColumnEditable(config, draftController)) return;
    const cell = findTableCell(rowId, columnId);
    if (cell) tableRef.current?.setEditingCell?.(cell);
  }, [draftController, findTableCell]);

  const activateCell = useCallback((record, columnId) => {
    const recordId = String(record.id);
    const openDetail = () => navigate(buildRevisionRapidaSaludRecordPath(convocatoriaId, recordId), {
      state: { ...sharedNavigationState, listState },
    });

    if (isMobile || record.source !== "LEGACY" || record.capabilities?.canEdit === false) {
      runGuarded(openDetail);
      return;
    }

    const activate = () => {
      if (activeRecordId === recordId && draftController.serverSnapshot) {
        enterCellEditing(recordId, columnId);
        return;
      }
      setActiveRecordId(recordId);
      setPendingCell({ rowId: recordId, columnId });
    };

    if (activeRecordId && activeRecordId !== recordId) runGuarded(activate);
    else activate();
  }, [
    activeRecordId,
    convocatoriaId,
    draftController.serverSnapshot,
    enterCellEditing,
    isMobile,
    listState,
    navigate,
    runGuarded,
    sharedNavigationState,
  ]);

  useEffect(() => {
    if (!pendingCell || draftController.initialLoading || !draftController.serverSnapshot) return;
    if (String(draftController.serverSnapshot.id) !== String(pendingCell.rowId)) return;
    enterCellEditing(pendingCell.rowId, pendingCell.columnId);
    setPendingCell(null);
  }, [draftController.initialLoading, draftController.serverSnapshot, enterCellEditing, pendingCell]);

  useEffect(() => {
    if (!pendingFocus || listFetching) return;
    if (!records.some((record) => String(record.id) === pendingFocus.rowId)) {
      setPendingFocus(null);
      return;
    }
    focusStaticCell(pendingFocus.rowId, pendingFocus.columnId);
    setPendingFocus(null);
  }, [focusStaticCell, pendingFocus, records, listFetching]);

  const moveEditor = useCallback((row, cell, table, direction) => {
    const target = getNextRevisionExcelCell(row, cell.column.id, direction, {
      diagnosesEditable: draftControllerRef.current.diagnosesEditable,
    });
    if (target) table.setEditingCell(target);
  }, []);

  const saveRow = useCallback(() => {
    if (draftController.dirty && draftController.canSave && !draftController.saving && !hasFrontendErrors) {
      draftController.executeMutation("save");
    }
  }, [draftController, hasFrontendErrors]);

  const verifyRow = useCallback(() => {
    if (draftController.canVerify && !draftController.saving && !hasFrontendErrors) {
      draftController.executeMutation("save_and_verify");
    }
  }, [draftController, hasFrontendErrors]);

  useEffect(() => {
    if (!activeRecordId) return undefined;
    const handleShortcut = (event) => {
      const shortcut = event.ctrlKey || event.metaKey;
      if (shortcut && event.key.toLowerCase() === "s") {
        event.preventDefault();
        saveRow();
      } else if (shortcut && event.key === "Enter") {
        event.preventDefault();
        verifyRow();
      } else if (event.key === "Escape" && !editingCell && !draftController.dirty) {
        closeActiveEditor();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [activeRecordId, closeActiveEditor, draftController.dirty, editingCell, saveRow, verifyRow]);

  const tableData = useMemo(
    () => records.map((record) => buildRevisionExcelRow(
      record,
      activeRecordId,
      draftController.draft,
      draftController.serverSnapshot
    )),
    [activeRecordId, draftController.draft, draftController.serverSnapshot, records]
  );

  const updateDraftCell = useCallback((config, value) => {
    if (config.kind === "slot") {
      draftController.setOrderedSlot(config.key, config.index, value);
      return;
    }
    draftController.setDraftField(config.section, getRevisionExcelFieldDefinition(config), value);
  }, [draftController]);

  const columns = useMemo(() => [
    { accessorKey: "id", header: "ID", size: 70, enableSorting: true, enableEditing: false },
    ...REVISION_EXCEL_EDITABLE_COLUMNS.map((config) => ({
      accessorKey: config.id,
      header: config.label,
      size: config.size,
      enableSorting: false,
      enableEditing: (row) =>
        String(row.original.id) === String(activeRecordId) &&
        isRevisionExcelColumnEditable(config, draftController),
      Cell: ({ cell, row }) => (
        <Box
          component="span"
          onDoubleClick={() => activateCell(row.original, config.id)}
          sx={{ display: "block", minHeight: 20 }}
        >
          {config.id === "edad"
            ? compactAgeLabel(cell.getValue())
            : (cell.getValue() ?? "") || "Sin dato"}
        </Box>
      ),
      Edit: ({ cell, row, table }) => (
        <RevisionExcelCellEditor
          config={config}
          value={row.original[config.id]}
          error={draftController.fieldErrors[getRevisionExcelFieldErrorKey(config)]}
          row={row}
          cell={cell}
          table={table}
          saving={draftController.saving}
          onChange={(value) => updateDraftCell(config, value)}
          onMove={moveEditor}
          onSave={saveRow}
          onVerify={verifyRow}
          onExit={() => focusStaticCell(row.id, config.id)}
        />
      ),
    })),
    {
      accessorKey: "diagnosticoResumen",
      header: "Diagnóstico resumen",
      size: 250,
      enableSorting: false,
      enableEditing: false,
      Cell: ({ cell }) => cell.getValue() || "Sin diagnóstico resumido",
    },
    {
      id: "medico",
      header: "Médico",
      size: 180,
      enableSorting: false,
      enableEditing: false,
      accessorFn: (row) => row.medico?.nombre || "Sin asignar",
    },
    {
      accessorKey: "tratamientos",
      header: "Tratamientos",
      size: 220,
      enableSorting: false,
      enableEditing: false,
      Cell: ({ cell }) => <Typography variant="body2" title={cell.getValue()}>{cell.getValue()}</Typography>,
    },
    {
      accessorKey: "source",
      header: "Source",
      size: 145,
      enableSorting: false,
      enableEditing: false,
      Cell: ({ row }) => (
        <Chip
          size="small"
          variant="outlined"
          color={row.original.source === "V2" ? "info" : "default"}
          label={row.original.source === "V2" ? "V2 · Solo lectura" : row.original.source || "Sin source"}
        />
      ),
    },
    {
      id: "status",
      header: "Estado",
      size: 140,
      enableSorting: false,
      enableEditing: false,
      accessorFn: (row) => getRevisionRecordStatus(row).label,
      Cell: ({ row }) => {
        const status = getRevisionRecordStatus(row.original);
        return <Chip size="small" color={status.color} label={status.label} />;
      },
    },
  ], [
    activeRecordId,
    activateCell,
    draftController,
    focusStaticCell,
    moveEditor,
    saveRow,
    updateDraftCell,
    verifyRow,
  ]);

  const handlePaginationChange = (updater) => {
    const next = resolveUpdater(updater, pagination);
    runGuarded(() => setPagination({
      pageIndex: Math.max(0, next.pageIndex),
      pageSize: [25, 50, 100].includes(next.pageSize) ? next.pageSize : 25,
    }));
  };

  const handleSortingChange = (updater) => {
    const next = resolveUpdater(updater, sorting);
    const allowed = next.find((item) => SORT_FIELD_MAP[item.id]);
    runGuarded(() => {
      setSorting(allowed ? [{ id: allowed.id, desc: allowed.desc !== false }] : INITIAL_SORTING);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    });
  };

  const updateFilter = (setter) => (event) => {
    const value = event.target.value;
    runGuarded(() => {
      setter(value);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    });
  };

  const handleSearchChange = (event) => {
    const value = event.target.value.slice(0, 100);
    runGuarded(() => {
      setSearchInput(value);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    });
  };

  const hasFilters = Boolean(stateFilter || sourceFilter || debouncedSearch);
  const listLoadingState = getRevisionListLoadingState({
    listFetching,
    hasCompletedInitialFetch: hasLoadedRecords,
    recordCount: records.length,
  });
  const clearFilters = () => runGuarded(() => {
    setStateFilter("");
    setSourceFilter("");
    setSearchInput("");
    setDebouncedSearch("");
    setPagination(INITIAL_PAGINATION);
    setSorting(INITIAL_SORTING);
  });
  const applyQuickState = (value) => runGuarded(() => {
    setStateFilter(value);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  });
  const handleRefresh = () => runGuarded(() => setRefreshVersion((current) => current + 1));

  const canManageTarget = resolveUxCapability({
    payloads: [summary, meta],
    capability: "MANAGE_TARGET",
    user,
  });
  const canManageMaterials = resolveUxCapability({
    payloads: [summary],
    capability: "MANAGE_MATERIALS",
    user,
  });
  const canManageParticipants = PARTICIPANT_ALLOWED_ROLES.includes(String(user?.rol || ""));

  const handleSaveMeta = async (expected) => {
    setMetaSaving(true);
    setMetaActionError("");
    try {
      const payload = { expected };
      if (meta?.configured) payload.expectedVersion = meta.version;
      await updateRevisionRapidaSaludMeta(convocatoriaId, payload);
      setMetaDialogOpen(false);
      setRefreshVersion((current) => current + 1);
    } catch (error) {
      setMetaActionError(extractRevisionApiError(error).message);
    } finally {
      setMetaSaving(false);
    }
  };

  const openCreateMaterial = () => {
    setEditingMaterial(null);
    setMaterialActionError("");
    setLinksDialogOpen(false);
    setMaterialDialogOpen(true);
  };

  const openEditMaterial = (material) => {
    setEditingMaterial(material);
    setMaterialActionError("");
    setLinksDialogOpen(false);
    setMaterialDialogOpen(true);
  };

  const handleSaveMaterial = async (payload) => {
    setMaterialSaving(true);
    setMaterialActionError("");
    try {
      if (editingMaterial) {
        if (editingMaterial.version === null || editingMaterial.version === undefined) {
          const conflictError = new Error("VERSION_CONFLICT");
          conflictError.response = { status: 409, data: { error: { code: "VERSION_CONFLICT" } } };
          throw conflictError;
        }
        await updateRevisionRapidaSaludMaterial(convocatoriaId, editingMaterial.id, {
          ...payload,
          expectedVersion: editingMaterial.version,
        });
      } else {
        await createRevisionRapidaSaludMaterial(convocatoriaId, payload);
      }
      setMaterialDialogOpen(false);
      setRefreshVersion((current) => current + 1);
    } catch (error) {
      setMaterialActionError(extractRevisionApiError(error).message);
    } finally {
      setMaterialSaving(false);
    }
  };

  const handleCopyMaterial = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setListSnackbar("Enlace copiado");
    } catch (error) {
      setListSnackbar("No se pudo copiar el enlace");
    }
  };

  const moveStaticFocus = useCallback((row, cell, table, key) => {
    const rows = table.getRowModel().rows;
    const visibleIds = table.getVisibleLeafColumns().map((column) => column.id);
    let rowIndex = rows.findIndex((item) => item.id === row.id);
    let columnIndex = visibleIds.indexOf(cell.column.id);
    if (key === "ArrowLeft") columnIndex -= 1;
    if (key === "ArrowRight") columnIndex += 1;
    if (key === "ArrowUp") rowIndex -= 1;
    if (key === "ArrowDown") rowIndex += 1;
    const targetRow = rows[Math.max(0, Math.min(rows.length - 1, rowIndex))];
    const targetColumn = visibleIds[Math.max(0, Math.min(visibleIds.length - 1, columnIndex))];
    if (targetRow && targetColumn) focusStaticCell(targetRow.id, targetColumn);
  }, [focusStaticCell]);

  const convocatoria = summary?.convocatoria || {};
  const projectLabel = convocatoria.projectName || stateContext.projectName ||
    (convocatoria.projectId ? `Proyecto #${convocatoria.projectId}` : "Proyecto no disponible");
  const participantConvocatoria = useMemo(() => {
    const current = summary?.convocatoria || {};
    return {
      ...current,
      id: current.id || convocatoriaId,
      idConvocatoria: current.id || convocatoriaId,
      idProyecto: current.projectId,
      nombreProyecto: current.projectName || stateContext.projectName,
      descripcionConvo: current.descripcion || current.actividad,
    };
  }, [convocatoriaId, stateContext.projectName, summary]);
  const emptyMessage = hasFilters
    ? "No hay registros que coincidan con los filtros seleccionados"
    : "No hay registros para revisar en esta convocatoria";

  const openFullDetail = (recordId) => runGuarded(() => navigate(
    buildRevisionRapidaSaludRecordPath(convocatoriaId, recordId),
    { state: { ...sharedNavigationState, listState } }
  ));

  const submitMarkError = async () => {
    const validation = validateMarkErrorReason(markErrorReason);
    setMarkErrorValidation(validation);
    if (validation) return;
    const result = await draftController.executeMutation("mark_error", { reason: markErrorReason.trim() });
    if (result) {
      setMarkErrorOpen(false);
      setMarkErrorReason("");
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: { xs: 2, md: 3 }, px: { xs: 1, sm: 2, md: 3 }, minHeight: "100%" }}>
      <Breadcrumbs aria-label="Navegación" sx={{ mb: 2 }}>
        <Link component="button" onClick={() => runGuarded(() => navigate("/transcripcion"))} underline="hover" color="inherit">
          Transcripción
        </Link>
        {!unified && (
          <Link
            component="button"
            onClick={() => runGuarded(() => navigate(buildRevisionRapidaSaludPath(convocatoriaId), {
              state: sharedNavigationState,
            }))}
            underline="hover"
            color="inherit"
          >
            Revisión de datos
          </Link>
        )}
        <Typography color="text.primary">{unified ? "Revisión rápida de Salud" : "Registros"}</Typography>
      </Breadcrumbs>

      <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" gap={2} mb={2}>
        <Box minWidth={0}>
          <Typography variant="h2" sx={{ fontSize: { xs: "1.8rem", md: "2.35rem" }, fontWeight: 900 }}>
            {unified ? "Revisión rápida de Salud" : "Registros de Revisión de Salud"}
          </Typography>
          {summaryLoading && !summary ? (
            <Stack direction="row" alignItems="center" gap={1} mt={1}>
              <CircularProgress size={18} />
              <Typography color="text.secondary">Cargando contexto...</Typography>
            </Stack>
          ) : (
            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
              {projectLabel} · Convocatoria #{convocatoria.id || convocatoriaId} · {formatActivity(convocatoria.actividad)} · {convocatoria.comunidad || "Comunidad sin definir"} · {formatDate(convocatoria.fecha)}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }} alignSelf={{ lg: "flex-start" }}>
          {!unified && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={() => runGuarded(() => navigate(buildRevisionRapidaSaludPath(convocatoriaId), {
                state: sharedNavigationState,
              }))}
            >
              Volver al resumen
            </Button>
          )}
          {unified && (
            <>
              <Button
                variant="outlined"
                startIcon={canManageTarget ? <EditOutlinedIcon /> : <FlagOutlinedIcon />}
                onClick={() => { setMetaActionError(""); setMetaDialogOpen(true); }}
                disabled={auxiliaryLoading && !meta}
              >
                Meta
              </Button>
              <Button
                variant="outlined"
                startIcon={canManageMaterials ? <AddLinkOutlinedIcon /> : <LinkOutlinedIcon />}
                onClick={() => setLinksDialogOpen(true)}
                disabled={auxiliaryLoading && materials.length === 0}
              >
                Enlaces
              </Button>
              {canManageParticipants && (
                <Button variant="outlined" startIcon={<GroupsOutlinedIcon />} onClick={() => setParticipantsDialogOpen(true)}>
                  Participantes
                </Button>
              )}
              {canManageParticipants && (
                <Tooltip title="Agregar participantes">
                  <Button
                    variant="outlined"
                    aria-label="Agregar participantes"
                    onClick={() => setAddParticipantsOpen(true)}
                    sx={{ minWidth: 40, px: 1 }}
                  >
                    <PersonAddAltOutlinedIcon />
                  </Button>
                </Tooltip>
              )}
            </>
          )}
          <Tooltip title="Actualizar datos">
            <span>
              <Button
                variant="contained"
                aria-label="Actualizar"
                onClick={handleRefresh}
                disabled={listFetching || auxiliaryLoading}
                sx={{ minWidth: 40, px: 1 }}
              >
                <RefreshOutlinedIcon />
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {summaryError && <Alert severity="warning" sx={{ mb: 2 }}>{summaryError}</Alert>}
      {recordsError && <Alert severity="error" sx={{ mb: 2 }}>{recordsError}</Alert>}

      {unified && (!mixed || summary?.progress) && (
        <Box component="section" aria-label="Resumen de revisión" sx={{ mb: 2 }}>
          <RevisionKpis summary={summary} />
        </Box>
      )}

      {!unified && <Paper variant="outlined" data-testid="revision-jornada-context" sx={{ p: 2, mb: 2, bgcolor: "background.paper" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1} mb={1.5}>
          <Typography variant="subtitle2" fontWeight={900}>DATOS DE LA JORNADA</Typography>
          <Typography variant="caption" color="text.secondary">Datos definidos por la convocatoria.</Typography>
        </Stack>
        <Divider sx={{ mb: 1.5 }} />
        <Stack direction="row" sx={{ flexWrap: "wrap" }} gap={1}>
          <Chip label={`Estado: ${jornadaContext.estado}`} variant="outlined" />
          <Chip label={`Municipio: ${jornadaContext.municipio}`} variant="outlined" />
          <Chip label={`Parroquia: ${jornadaContext.parroquia}`} variant="outlined" />
          <Chip label={`Establecimiento: ${jornadaContext.establecimiento}`} variant="outlined" />
          <Chip label={`Fecha de atención: ${formatDate(jornadaContext.fechaAtencion)}`} variant="outlined" />
        </Stack>
      </Paper>}

      <Paper variant="outlined" data-testid="revision-filter-panel" sx={{ p: { xs: 1.25, md: 1.5 }, mb: 1.5, bgcolor: "background.paper" }}>
        {unified && (
          <Stack direction="row" sx={{ flexWrap: "wrap", mb: 1.25 }} gap={0.75}>
            <Chip
              label="Todos"
              color={stateFilter === "" ? "primary" : "default"}
              variant={stateFilter === "" ? "filled" : "outlined"}
              onClick={() => applyQuickState("")}
            />
            <Chip
              label="Solo pendientes"
              color={stateFilter === "0" ? "warning" : "default"}
              variant={stateFilter === "0" ? "filled" : "outlined"}
              onClick={() => applyQuickState("0")}
            />
            <Chip
              label="Con errores"
              color={stateFilter === "3" ? "error" : "default"}
              variant={stateFilter === "3" ? "filled" : "outlined"}
              onClick={() => applyQuickState("3")}
            />
          </Stack>
        )}
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              label="Buscar por código, cédula, nombre o apellido"
              value={searchInput}
              onChange={handleSearchChange}
              fullWidth
              size="small"
              inputProps={{ maxLength: 100 }}
              error={searchInput.trim().length === 1}
              helperText={searchInput.trim().length === 1 ? "Escribe al menos 2 caracteres." : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField select label="Estado" value={stateFilter} onChange={updateFilter(setStateFilter)} fullWidth size="small">
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="0">Pendientes</MenuItem>
              <MenuItem value="1">Verificados</MenuItem>
              <MenuItem value="3">Errores</MenuItem>
              <MenuItem value="2">Sin clasificar</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField select label="Source" value={sourceFilter} onChange={updateFilter(setSourceFilter)} fullWidth size="small">
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="LEGACY">LEGACY</MenuItem>
              <MenuItem value="V2">V2</MenuItem>
              {mixed && <MenuItem value="V2_CAPTURE">Capturas Salud V2</MenuItem>}
            </TextField>
          </Grid>
        </Grid>
        {hasFilters && <Button onClick={clearFilters} size="small" sx={{ mt: 0.5 }}>Limpiar filtros</Button>}
      </Paper>

      {activeRecordId && (
        <Paper
          variant="outlined"
          data-testid="active-row-toolbar"
          sx={{ position: "sticky", top: 8, zIndex: 5, p: 1.5, mb: 1.5, bgcolor: "background.paper" }}
        >
          <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" alignItems={{ lg: "center" }} gap={1.5}>
            <Stack direction="row" alignItems="center" sx={{ flexWrap: "wrap" }} gap={1}>
              <Typography fontWeight={900}>Registro #{activeRecordId}</Typography>
              {draftController.initialLoading ? (
                <Chip icon={<CircularProgress size={14} />} label="Cargando detalle" size="small" />
              ) : (
                <Chip
                  color={draftController.dirty ? "primary" : "default"}
                  label={`${changedColumnIds.length} ${changedColumnIds.length === 1 ? "cambio" : "cambios"} sin guardar`}
                  size="small"
                />
              )}
              {draftController.source && <Chip label={draftController.source} size="small" variant="outlined" />}
            </Stack>
            <Stack direction="row" sx={{ flexWrap: "wrap" }} gap={1}>
              <Button onClick={draftController.resetDraft} disabled={!draftController.dirty || draftController.saving}>Cancelar</Button>
              <Button
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                onClick={saveRow}
                disabled={!draftController.dirty || !draftController.canSave || draftController.saving || hasFrontendErrors}
              >
                Guardar
              </Button>
              <Button
                color="error"
                variant="outlined"
                startIcon={<ErrorOutlineOutlinedIcon />}
                onClick={() => setMarkErrorOpen(true)}
                disabled={!draftController.canMarkError || draftController.saving}
              >
                Marcar error
              </Button>
              <Button
                variant="contained"
                startIcon={<FactCheckOutlinedIcon />}
                onClick={verifyRow}
                disabled={!draftController.canVerify || draftController.saving || hasFrontendErrors}
              >
                Guardar y verificar
              </Button>
              <Button startIcon={<OpenInNewOutlinedIcon />} onClick={() => openFullDetail(activeRecordId)}>
                Detalle completo
              </Button>
            </Stack>
          </Stack>
          {draftController.error && <Alert severity="error" sx={{ mt: 1.5 }}>{draftController.error}</Alert>}
        </Paper>
      )}

      {(!mixed || sourceFilter !== "V2_CAPTURE") && <>
      <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" mb={1}>
        <Typography fontWeight={800}>Listado operativo</Typography>
        <Typography color="text.secondary" variant="body2">
          {serverPagination.total} registros · Página {serverPagination.page} de {serverPagination.totalPages}
        </Typography>
      </Box>

      <MaterialReactTable
        columns={columns}
        data={tableData}
        getRowId={getRevisionTableRowId}
        localization={{ ...MRT_Localization_ES, noRecordsToDisplay: emptyMessage }}
        tableInstanceRef={tableRef}
        editingMode="cell"
        enableEditing={!isMobile}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableGlobalFilter={false}
        enableHiding
        enableRowActions
        initialState={{ density: "compact" }}
        manualFiltering
        manualPagination
        manualSorting
        onColumnVisibilityChange={setColumnVisibility}
        onEditingCellChange={setEditingCell}
        onPaginationChange={handlePaginationChange}
        onSortingChange={handleSortingChange}
        positionActionsColumn="last"
        rowCount={Number(serverPagination.total || 0)}
        renderRowActionMenuItems={({ row, closeMenu }) => {
          const record = row.original;
          const actionLabel = getRevisionRecordActionLabel(record);
          const ActionIcon = actionLabel === "Revisar" ? FactCheckOutlinedIcon : VisibilityOutlinedIcon;
          const items = [
            <MenuItem key="revisar" onClick={() => { closeMenu?.(); activateCell(record, "nombre"); }}>
              <ActionIcon sx={{ mr: 1 }} />
              {actionLabel}
            </MenuItem>,
          ];
          if (!isMobile && record.source === "LEGACY") {
            items.push(
              <MenuItem key="detalle" onClick={() => { closeMenu?.(); openFullDetail(record.id); }}>
                <OpenInNewOutlinedIcon sx={{ mr: 1 }} />
                Detalle completo
              </MenuItem>
            );
          }
          return items;
        }}
        renderEmptyRowsFallback={() => (
          <Box textAlign="center" py={4} px={2}>
            <Typography color="text.secondary">{emptyMessage}</Typography>
            {hasFilters && <Button onClick={clearFilters}>Limpiar filtros</Button>}
          </Box>
        )}
        state={{
          columnVisibility,
          editingCell,
          isLoading: listLoadingState.initialListLoading,
          pagination,
          showProgressBars: listLoadingState.showProgressBars,
          showSkeletons: listLoadingState.showSkeletons,
          sorting,
        }}
        muiTablePaginationProps={{ rowsPerPageOptions: [25, 50, 100] }}
        muiTableContainerProps={{ sx: { overflowX: "auto", bgcolor: "background.paper" } }}
        muiTableHeadCellProps={{ sx: { fontWeight: 900, bgcolor: "grey.50", borderColor: "divider" } }}
        muiTableBodyCellProps={({ cell, row, table }) => {
          const columnId = cell.column.id;
          const rowId = String(row.id);
          const isChanged = rowId === String(activeRecordId) && changedColumnSet.has(columnId);
          const editableConfig = REVISION_EXCEL_COLUMN_BY_ID[columnId];
          return {
            ref: (node) => {
              if (node) cellRefs.current[`${rowId}:${columnId}`] = node;
              else delete cellRefs.current[`${rowId}:${columnId}`];
            },
            tabIndex: 0,
            "aria-label": `${editableConfig?.label || columnId}, registro ${rowId}`,
            "aria-selected": rowId === String(activeRecordId),
            onDoubleClick: () => {
              if (editableConfig) activateCell(row.original, columnId);
            },
            onKeyDown: (event) => {
              if (["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName)) return;
              if (event.key === "Enter" && editableConfig) {
                event.preventDefault();
                activateCell(row.original, columnId);
              } else if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
                event.preventDefault();
                moveStaticFocus(row, cell, table, event.key);
              }
            },
            sx: {
              bgcolor: isChanged ? "action.selected" : "background.paper",
              borderColor: "divider",
              borderLeft: isChanged ? `3px solid ${theme.palette.primary.main}` : undefined,
              py: 1,
              "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
            },
          };
        }}
        muiTableBodyRowProps={({ row }) => ({
          "aria-selected": String(row.id) === String(activeRecordId),
          sx: {
            bgcolor: "background.paper",
            "&:hover td": { bgcolor: "action.hover" },
          },
        })}
        muiTablePaperProps={{
          variant: "outlined",
          elevation: 0,
          "data-testid": "revision-records-table-paper",
          sx: { borderRadius: 1, overflow: "hidden", bgcolor: "background.paper" },
        }}
      />

      </>}
      {mixed && !["LEGACY", "V2"].includes(sourceFilter) && <React.Suspense fallback={<CircularProgress size={24} />}>
        <V2CaptureList key={convocatoriaId} convocatoriaId={convocatoriaId}
          stateFilter={stateFilter} search={debouncedSearch} refreshVersion={refreshVersion}
          onSaved={() => setRefreshVersion(value => value + 1)} />
      </React.Suspense>}

      {unified && (
        <>
          <MetaDialog
            open={metaDialogOpen}
            meta={meta}
            loading={metaSaving}
            apiError={metaActionError || metaError}
            readOnly={!canManageTarget}
            onClose={() => !metaSaving && setMetaDialogOpen(false)}
            onSave={handleSaveMeta}
          />
          <RevisionLinksDialog
            open={linksDialogOpen}
            materials={materials}
            loading={auxiliaryLoading}
            error={materialsError}
            canManage={canManageMaterials}
            onClose={() => setLinksDialogOpen(false)}
            onCreate={openCreateMaterial}
            onEdit={openEditMaterial}
            onCopy={handleCopyMaterial}
          />
          <MaterialDialog
            open={materialDialogOpen}
            material={editingMaterial}
            loading={materialSaving}
            apiError={materialActionError}
            onClose={() => !materialSaving && setMaterialDialogOpen(false)}
            onSave={handleSaveMaterial}
          />
          <React.Suspense fallback={null}>
            {participantsDialogOpen && (
              <VoluntariosConvocatoriaOperativaDialog
                open
                onClose={() => setParticipantsDialogOpen(false)}
                convocatoria={participantConvocatoria}
                allowedRoles={PARTICIPANT_ALLOWED_ROLES}
              />
            )}
            {addParticipantsOpen && (
              <AgregarParticipantesConvocatoriaModal
                open
                onClose={() => setAddParticipantsOpen(false)}
                convocatoria={participantConvocatoria}
                onSuccess={() => setListSnackbar("Participantes actualizados")}
                allowedRoles={PARTICIPANT_ALLOWED_ROLES}
              />
            )}
          </React.Suspense>
        </>
      )}

      <Dialog open={Boolean(pendingAction)} onClose={() => setPendingAction(null)}>
        <DialogTitle>Tienes cambios sin guardar</DialogTitle>
        <DialogContent>
          <Typography>La acción solicitada descartará los cambios de la fila activa.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)}>Seguir editando</Button>
          <Button color="error" variant="contained" onClick={confirmPendingAction}>
            Descartar cambios y cambiar de fila
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={markErrorOpen} onClose={() => !draftController.saving && setMarkErrorOpen(false)}>
        <DialogTitle>Marcar registro con error</DialogTitle>
        <DialogContent>
          {draftController.dirty && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Los cambios clínicos locales no se incluirán al marcar el registro con error.
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            margin="dense"
            label="Motivo"
            value={markErrorReason}
            onChange={(event) => { setMarkErrorReason(event.target.value); setMarkErrorValidation(""); }}
            error={Boolean(markErrorValidation)}
            helperText={markErrorValidation || "Describe por qué el registro requiere corrección."}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkErrorOpen(false)} disabled={draftController.saving}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={submitMarkError} disabled={draftController.saving}>
            Marcar error
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(draftController.stateDialog)} onClose={() => draftController.setStateDialog(null)}>
        <DialogTitle>{draftController.stateDialog?.code === "VERSION_CONFLICT" ? "Conflicto de versión" : "Estado actualizado"}</DialogTitle>
        <DialogContent><Typography>{draftController.stateDialog?.message}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => draftController.setStateDialog(null)}>Seguir viendo</Button>
          {draftController.stateDialog?.code === "VERSION_CONFLICT" && (
            <Button variant="contained" onClick={() => { draftController.setStateDialog(null); draftController.loadDetail(); }}>
              Recargar información
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(listSnackbar)} autoHideDuration={3500} onClose={() => setListSnackbar("")} message={listSnackbar} />
    </Container>
  );
};

export default RegistrosRevisionRapidaSalud;
